import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';

/**
 * LeaveRequestsWidget - Widget "Demandes de congés" pour RH
 */
const LeaveRequestsWidget = ({ token, onNavigate }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRequests = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/leave-requests?status=submitted&limit=5`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) throw new Error('Erreur de chargement');

        const data = await response.json();
        setRequests(data.success ? (data.data || []) : []);
      } catch (err) {
        console.error('Erreur fetch leave requests:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [token]);

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const startStr = startDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

    if (start === end) {
      return startStr;
    }

    const endStr = endDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    return `${startStr} - ${endStr}`;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Demandes de congés</h4>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Demandes de congés</h4>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>
          Demandes de congés
          {requests.length > 0 && (
            <span style={styles.count}>({requests.length})</span>
          )}
        </h4>
        <button
          style={styles.viewButton}
          onClick={() => onNavigate && onNavigate('leave-requests')}
        >
          Voir demandes
        </button>
      </div>

      {requests.length === 0 ? (
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>📋</span>
          <p style={styles.emptyText}>Aucune demande en attente</p>
        </div>
      ) : (
        <div style={styles.list}>
          {requests.map((req, index) => (
            <div key={req.id || index} style={styles.requestItem}>
              <div style={styles.requestInfo}>
                <span style={styles.userName}>{req.user_name}</span>
                <span style={styles.typeBadge}>{req.leave_type_code || 'CP'}</span>
                <span style={styles.dates}>{formatDateRange(req.start_date, req.end_date)}</span>
                {req.days_count && (
                  <span style={styles.daysCount}>
                    ({req.days_count}j)
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  count: {
    backgroundColor: '#17a2b8',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: '500'
  },
  viewButton: {
    background: 'none',
    border: 'none',
    color: '#6366f1',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6c757d'
  },
  error: {
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545',
    textAlign: 'center'
  },
  empty: {
    textAlign: 'center',
    padding: '30px'
  },
  emptyIcon: {
    fontSize: '2rem',
    display: 'block',
    marginBottom: '8px'
  },
  emptyText: {
    margin: 0,
    color: '#6c757d'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  requestItem: {
    padding: '12px',
    backgroundColor: '#e8f4fd',
    borderRadius: '8px',
    border: '1px solid #bee5eb'
  },
  requestInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap'
  },
  userName: {
    fontWeight: '600',
    fontSize: '0.9rem',
    color: '#1a1a2e'
  },
  typeBadge: {
    padding: '2px 8px',
    backgroundColor: '#17a2b8',
    color: 'white',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '600'
  },
  dates: {
    fontSize: '0.85rem',
    color: '#495057'
  },
  daysCount: {
    fontSize: '0.8rem',
    color: '#6c757d'
  }
};

export default LeaveRequestsWidget;
